import { memo } from 'react';
import { Heart } from 'lucide-react';
import { formatDate, formatUserName } from '@/lib';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@/components';
import { Card } from '@/components/ui/card';
import { ImageWithFallback } from '@/components';
import type { FavoriteWishlistUpdatedPost } from '../model';

interface FavoriteWishlistUpdatedCardProps {
  post: FavoriteWishlistUpdatedPost;
  onItemClick: (wishlistId: string, itemId: string) => void;
  onUserClick: (userId: string) => void;
}

/**
 * Карточка обновления избранного вишлиста
 */
export const FavoriteWishlistUpdatedCard = memo(function FavoriteWishlistUpdatedCard({
  post,
  onItemClick,
  onUserClick,
}: FavoriteWishlistUpdatedCardProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="pt-4 pb-4 px-0 gap-0">
      {/* Заголовок с аватаром и иконкой избранного */}
      <div className="flex items-start gap-4 mb-4 px-4">
        <button
          type="button"
          className="relative flex-shrink-0"
          onClick={() => onUserClick(post.owner.id)}
          aria-label={post.owner.username || undefined}
        >
          <UserAvatar
            userId={post.owner.id}
            avatarUrl={post.owner.avatarUrl}
            displayName={post.owner.displayName}
            lastName={post.owner.lastName}
            username={post.owner.username}
            size="default"
          />
          {/* Иконка сердечка в углу */}
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white flex items-center justify-center">
            <Heart className="text-red-500 fill-red-500" style={{ width: 9, height: 9 }} />
          </div>
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground">
            <button
              type="button"
              className="font-semibold"
              onClick={() => onUserClick(post.owner.id)}
            >
              {formatUserName(post.owner.displayName, post.owner.lastName, { short: true }) || post.owner.username}
            </button>{' '}
            <span className="text-muted-foreground">
              {t('feed:feed.favoriteWishlistUpdated')}
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {formatDate(post.createdAt)}
          </p>
        </div>
      </div>

      {/* Желание */}
      <button
        type="button"
        className="active:opacity-80 transition-opacity w-full text-left"
        onClick={() => onItemClick(post.item.wishlistId, post.item.id)}
      >
        {/* Изображение если есть */}
        {post.item.imageUrl && (
          <div className="overflow-hidden mb-4">
            <ImageWithFallback
              src={post.item.imageUrl}
              alt={post.item.title}
              loading="lazy"
              className="w-full aspect-square object-cover"
            />
          </div>
        )}

        {/* Название желания */}
        <p className="font-semibold text-sm text-foreground px-4">{post.item.title}</p>

        {/* Название вишлиста */}
        <p className="text-xs text-muted-foreground mt-0.5 px-4">
          {t('feed:feed.inWishlist')} «{post.wishlist.title}»
        </p>
      </button>
    </Card>
  );
});